import { useActiveDataflowId } from "@/stores/dataflow";
import { useUIStore } from "@/stores/ui";
import { useCallback } from "react";
import {
  WorkspaceModalDataflow,
  WorkspaceModalEnhancement,
  WorkspaceModalGroupConfig,
  WorkspaceModalMapCluster,
  WorkspaceModalMapData,
  WorkspaceModalMapFolder,
  WorkspaceModalMapGroup,
  WorkspaceModalMapPoint,
  WorkspaceModalMapVisualization,
  WorkspaceModalPaste,
  WorkspaceModalProject,
  WorkspaceModalSave,
} from "../";

const defaultModal = {
  mode: null,
  isOpen: false,
  title: undefined,
  data: undefined,
} as const;

export function WorkspaceModal({ defaultProjectId }: { defaultProjectId?: string }) {
  const dataflowId = useActiveDataflowId();
  const modalSelector = useCallback(
    (state: any) => {
      if (!dataflowId) {
        return state.getGlobalModal();
      }
      const workspaceUI = state.getWorkspaceUI(dataflowId);
      return workspaceUI?.modal ?? defaultModal;
    },
    [dataflowId]
  );
  const modal = useUIStore(modalSelector);
  const closeModal = useUIStore((state) => state.closeModal);

  if (!modal.isOpen || !modal.mode) {
    return null;
  }

  const handleClose = () => closeModal(dataflowId || undefined);

  switch (modal.mode) {
    case "workspace": {
      const workspaceData = modal.data as
        | { defaultProjectId?: string }
        | undefined;
      return (
        <WorkspaceModalDataflow
          onClose={handleClose}
          defaultProjectId={workspaceData?.defaultProjectId || defaultProjectId}
          title={modal.title}
        />
      );
    }
    case "project":
      return (
        <WorkspaceModalProject onClose={handleClose} title={modal.title} />
      );
    case "geocode":
      // Redirect to enhancement modal with geocoding service (backward compatibility)
      return (
        <WorkspaceModalEnhancement
          onClose={handleClose}
          title={modal.title || "Geocode Dataset"}
          serviceId="geocoding"
        />
      );
    case "enhancement": {
      const enhancementData = modal.data as { serviceId: string } | undefined;
      if (!enhancementData?.serviceId) {
        // Service ID is required - close modal if not provided
        handleClose();
        return null;
      }
      return (
        <WorkspaceModalEnhancement
          onClose={handleClose}
          title={modal.title || "Enhance Data"}
          serviceId={enhancementData.serviceId}
        />
      );
    }
    case "paste":
      return (
        <WorkspaceModalPaste
          opened={modal.isOpen}
          onClose={handleClose}
          title={modal.title}
        />
      );
    case "group-config": {
      const groupData = modal.data as
        | {
            groupValue?: string;
            initialName?: string;
            initialColor?: string;
            initialShape?: "circle" | "square" | "triangle" | "star" | "diamond";
            initialVisible?: boolean;
            onVisibilityChange?: (groupValue: string, visible: boolean) => void;
          }
        | undefined;
      return (
        <WorkspaceModalGroupConfig
          onClose={handleClose}
          title={modal.title || "Configure Group"}
          groupValue={groupData?.groupValue}
          initialName={groupData?.initialName}
          initialColor={groupData?.initialColor}
          initialShape={groupData?.initialShape}
        />
      );
    }
    case "save":
      return (
        <WorkspaceModalSave
          opened={modal.isOpen}
          onClose={handleClose}
          title={modal.title}
        />
      );
    case "map-cluster": {
      const clusterData = modal.data as
        | {
            chunkValue?: string;
            initialDisplayName?: string;
            initialFrom?: string;
            initialTo?: string;
            initialColor?: string;
            initialShape?: "circle" | "square" | "triangle" | "star" | "diamond";
            initialVisible?: boolean;
            isLayerRegion?: boolean;
          }
        | undefined;
      return (
        <WorkspaceModalMapCluster
          onClose={handleClose}
          title={modal.title || "Configure Cluster"}
          chunkValue={clusterData?.chunkValue}
          initialDisplayName={clusterData?.initialDisplayName}
          initialFrom={clusterData?.initialFrom}
          initialTo={clusterData?.initialTo}
          initialColor={clusterData?.initialColor}
          initialShape={clusterData?.initialShape}
          initialVisible={clusterData?.initialVisible}
          isLayerRegion={clusterData?.isLayerRegion}
        />
      );
    }
    case "map-data": {
      const dataModalData = modal.data as
        | {
            initialName?: string;
            initialVisible?: boolean;
            initialColor?: string;
            initialShape?: "circle" | "square" | "triangle" | "star" | "diamond";
            hasGroupsOrClusters?: boolean;
          }
        | undefined;
      return (
        <WorkspaceModalMapData
          onClose={handleClose}
          title={modal.title || "Configure Data Input"}
          initialName={dataModalData?.initialName}
          initialVisible={dataModalData?.initialVisible}
          initialColor={dataModalData?.initialColor}
          initialShape={dataModalData?.initialShape}
          hasGroupsOrClusters={dataModalData?.hasGroupsOrClusters}
        />
      );
    }
    case "map-folder": {
      const folderData = modal.data as
        | {
            folderId?: string;
            initialName?: string;
          }
        | undefined;
      return (
        <WorkspaceModalMapFolder
          onClose={handleClose}
          title={modal.title || "Configure Folder"}
          folderId={folderData?.folderId}
          initialName={folderData?.initialName}
        />
      );
    }
    case "map-group": {
      const groupData = modal.data as
        | {
            initialColumnName?: string | null;
          }
        | undefined;
      return (
        <WorkspaceModalMapGroup
          onClose={handleClose}
          title={modal.title || "Configure Group"}
          initialColumnName={groupData?.initialColumnName}
        />
      );
    }
    case "map-point": {
      const pointData = modal.data as
        | {
            pointId?: string;
            pointValue?: string;
            initialColor?: string;
            initialShape?: "circle" | "square" | "triangle" | "star" | "diamond";
            initialVisible?: boolean;
          }
        | undefined;
      return (
        <WorkspaceModalMapPoint
          onClose={handleClose}
          title={modal.title || "Configure Point"}
          pointId={pointData?.pointId}
          pointValue={pointData?.pointValue}
          initialColor={pointData?.initialColor}
          initialShape={pointData?.initialShape}
          initialVisible={pointData?.initialVisible}
        />
      );
    }
    case "map-visualization":
      return (
        <WorkspaceModalMapVisualization
          onClose={handleClose}
          title={modal.title || "Configure Visualization Mode"}
        />
      );
    default:
      return null;
  }
}
